import {
  Integration,
  LastSyncJobInfo,
  getAccountingIntegrationStats,
  AccountingIntegrationStats,
  isAccountingIntegrationName,
} from "@/api/integration"
import { formatAccountingStatsParts } from "./accounting_sync_banner"
import { allIntegrations } from "@/setup/connect/integrations"
import { isKnownIntegrationName } from "@/setup/connect/auth_type"
import { capitalCase } from "change-case"
import { useLocation } from "wouter-preact"
import { CheckCircleIcon, ExclamationTriangleIcon } from "@heroicons/react/20/solid"
import { AUTH_FAILURE_LABEL, is_auth_failure, is_on_demand_connection, is_recent_sync } from "./sync_health"
import useSWR from "swr"

interface ConnectionCardProps {
  integration: Integration
  lastJob?: LastSyncJobInfo
}

function formatTimeAgo(dateString: string, now: Date = new Date()): string {
  const date = new Date(dateString)
  const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60))

  if (diffInHours < 1) {
    return "Just now"
  } else if (diffInHours < 24) {
    return `${diffInHours}h ago`
  } else {
    const diffInDays = Math.floor(diffInHours / 24)
    return `${diffInDays}d ago`
  }
}

export interface SyncStatus {
  label: string
  isError: boolean
  /** Synced inside the generous window — worth an affirmative tick rather than silence. */
  isHealthy?: boolean
}

/**
 * Status shown on a connection card. Everyone in the brand settings app — including
 * system admins sitting in it — sees when a sync last ran, not raw errors or staleness.
 * Those are operational and live in admin. The exception is a credential the provider
 * has rejected, which nobody but the brand can fix. A sync inside
 * `HEALTHY_SYNC_WINDOW_DAYS` reads as healthy, so a connection that is simply between
 * deliveries looks fine instead of looking like nothing at all.
 */
export function syncStatus(
  lastJob: LastSyncJobInfo | undefined,
  integrationName: string,
  now: Date = new Date()
): SyncStatus {
  if (is_on_demand_connection(integrationName)) {
    if (!lastJob) {
      return { label: "Synced", isError: false, isHealthy: true }
    }
    return {
      label: `Synced ${formatTimeAgo(lastJob.created_at, now)}`,
      isError: false,
      isHealthy: true,
    }
  }
  if (!lastJob) {
    return { label: "Not synced yet", isError: false }
  }

  const syncedAt = new Date(lastJob.created_at)
  const syncedLabel = `Synced ${formatTimeAgo(lastJob.created_at, now)}`
  const isHealthy = is_recent_sync(syncedAt, now)
  // A rejected credential keeps the job running daily, so it stays inside the healthy
  // window forever while syncing nothing. Recency cannot see that.
  if (is_auth_failure(lastJob.error)) {
    return { label: AUTH_FAILURE_LABEL, isError: true }
  }
  return { label: syncedLabel, isError: false, isHealthy }
}

function AccountingTotalStats({ integrationId }: { integrationId: string }) {
  const { data } = useSWR<ReturnType<typeof getAccountingIntegrationStats>>(
    `accounting-stats-${integrationId}`,
    () => getAccountingIntegrationStats(integrationId)
  )

  const stats: AccountingIntegrationStats | null = data && (data as any).ok ? (data as any).value.data : null
  if (!stats) return null

  const parts = formatAccountingStatsParts(stats)
  if (parts.length === 0) return <span class="text-xs text-gray-400">No data synced yet</span>
  return <span class="text-xs text-gray-500 font-medium">{parts.join(" · ")}</span>
}

export function ConnectionCard({ integration, lastJob }: ConnectionCardProps) {
  const [, setLocation] = useLocation()

  const config = allIntegrations.find(i => i.id === integration.name)
  const displayName = config?.name ?? capitalCase(integration.name)
  const logo = config?.logo
  const settingsPath = config?.settingsPath
    ?? (isKnownIntegrationName(integration.name)
      ? `/settings/config/${encodeURIComponent(integration.name)}`
      : `/settings/config/id/${integration.id}`)

  const status = syncStatus(lastJob, integration.name)

  function handleClick() {
    setLocation(settingsPath)
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      class="group relative flex items-center gap-5 p-5 bg-white border border-gray-200 rounded-xl hover:border-gray-300 hover:shadow-md transition-all cursor-pointer text-left w-full">
      {/* Logo */}
      {logo ? (
        <img src={logo} alt={displayName} class="h-12 w-12 object-contain" />
      ) : (
        <div class="h-12 w-12 bg-gray-100 rounded-lg flex items-center justify-center text-xl font-bold text-gray-600">
          {displayName.charAt(0)}
        </div>
      )}

      {/* Content */}
      <div class="flex-1 min-w-0">
        <div class="text-base font-semibold text-gray-900 mb-1">{displayName}</div>
        <div class="text-sm text-gray-500 truncate">{integration.username}</div>
      </div>

      {/* Status */}
      <div class="flex flex-col items-end gap-1 text-sm">
        <div class="flex items-center gap-2">
          {status.isError && <ExclamationTriangleIcon class="h-4 w-4 text-red-500" />}
          {!status.isError && status.isHealthy && <CheckCircleIcon class="h-4 w-4 text-green-600" />}
          <span class={status.isError ? "text-red-500" : status.isHealthy ? "text-green-700" : "text-gray-400"}>
            {status.label}
          </span>
        </div>
        {isAccountingIntegrationName(integration.name) && (
          <AccountingTotalStats integrationId={integration.id} />
        )}
      </div>
    </button>
  )
}
